import Layout from './Layout'
import { formatCurrency } from '../lib/format'
import { useState } from 'react'

interface Stats {
  today_sales: number
  today_revenue: number
  total_products: number
  low_stock_count: number
}

interface CustomerInfo {
  id: number
  name: string
  email: string | null
  tax_id: string | null
  phone: string | null
  city: string | null
  created_at: string
}

interface Account {
  id: number
  name: string
  slug: string
}

interface Props {
  account: Account
  catalog_url: string
  stats: Stats
  show_costs: boolean
  user_role: string
  recent_customers: CustomerInfo[]
  permissions?: {
    can_view_dashboard: boolean
  }
}

const roleLabels: Record<string, string> = {
  super_admin: 'Super Admin',
  admin: 'Administrador',
  supervisor: 'Supervisor',
  facturador: 'Facturador',
}

export default function Dashboard({ account, catalog_url, stats, show_costs, user_role, permissions, recent_customers }: Props) {
  const [copied, setCopied] = useState(false)

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(catalog_url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback: select the input text
      const input = document.getElementById('catalog-url-input') as HTMLInputElement
      if (input) {
        input.select()
        input.setSelectionRange(0, 99999)
      }
    }
  }

  return (
    <Layout account={account} permissions={permissions}>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-[#202020]">Dashboard</h1>
        <span className="text-sm text-gray-500">
          Rol: {roleLabels[user_role] || user_role}
        </span>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow">
          <p className="text-sm text-gray-500 mb-1">Ventas hoy</p>
          <p className="text-3xl font-bold text-[#202020]">{stats.today_sales}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <p className="text-sm text-gray-500 mb-1">Ingresos hoy</p>
          <p className="text-3xl font-bold text-[#55ACEE]">{formatCurrency(Number(stats.today_revenue))}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <p className="text-sm text-gray-500 mb-1">Total productos</p>
          <p className="text-3xl font-bold text-[#202020]">{stats.total_products}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <p className="text-sm text-gray-500 mb-1">Stock bajo</p>
          <p className={`text-3xl font-bold ${stats.low_stock_count > 0 ? 'text-red-600' : 'text-green-600'}`}>
            {stats.low_stock_count}
          </p>
        </div>
      </div>

      {!show_costs && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg mb-6">
          <p className="text-sm">
            No tienes permiso para ver los costos. Contacta a un administrador para acceder a esta información.
          </p>
        </div>
      )}

      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold text-[#202020] mb-4">Bienvenido a {account.name}</h2>
        <p className="text-gray-600">
          Utiliza el menú superior para navegar entre las diferentes secciones de tu punto de venta.
        </p>
      </div>

      <div className="bg-white p-6 rounded-lg shadow mt-8">
        <h2 className="text-xl font-semibold text-[#202020] mb-4">Catálogo público</h2>
        <p className="text-gray-600 mb-3">
          Comparte este enlace para que tus clientes vean tu catálogo en línea:
        </p>
        <div className="flex gap-2">
          <input
            id="catalog-url-input"
            type="text"
            readOnly
            value={catalog_url}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-[#55ACEE]"
            onClick={(e) => (e.target as HTMLInputElement).select()}
          />
          <button
            onClick={copyToClipboard}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              copied
                ? 'bg-green-500 text-white'
                : 'bg-[#55ACEE] text-white hover:bg-[#4a9dd6]'
            }`}
          >
            {copied ? '¡Copiado!' : 'Copiar'}
          </button>
        </div>
      </div>

      {recent_customers.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow mt-8">
          <h2 className="text-xl font-semibold text-[#202020] mb-4">Clientes registrados recientemente</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="border-b text-gray-500 uppercase text-xs">
                  <th className="py-2 pr-4">Nombre</th>
                  <th className="py-2 pr-4">Email</th>
                  <th className="py-2 pr-4">DNI/RUC</th>
                  <th className="py-2 pr-4">Teléfono</th>
                  <th className="py-2 pr-4">Ciudad</th>
                  <th className="py-2 pr-4">Fecha de registro</th>
                </tr>
              </thead>
              <tbody>
                {recent_customers.map((c) => (
                  <tr key={c.id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="py-2 pr-4 font-medium">{c.name}</td>
                    <td className="py-2 pr-4 text-gray-600">{c.email || '—'}</td>
                    <td className="py-2 pr-4 text-gray-600">{c.tax_id || '—'}</td>
                    <td className="py-2 pr-4 text-gray-600">{c.phone || '—'}</td>
                    <td className="py-2 pr-4 text-gray-600">{c.city || '—'}</td>
                    <td className="py-2 pr-4 text-gray-600">
                      {new Date(c.created_at).toLocaleDateString('es-HN', {
                        year: 'numeric', month: 'short', day: 'numeric',
                        hour: '2-digit', minute: '2-digit'
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Layout>
  )
}
